/**
 * AI clinical documentation API client.
 */
import apiClient, { buildWebSocketUrl, getAccessToken } from "@/api/client";
import type { AIChartContext, AITemplateType, ScribeNoteSections } from "@/lib/ai-chart-context";

export interface GenerateBHNoteRequest {
  encounter_id?: string;
  template_type: AITemplateType;
  chief_complaint?: string;
  patient_context?: AIChartContext;
  prompt?: string;
  /** Set only when retrying a scribe draft whose socket dropped. */
  scribe_session_id?: string;
}

export interface GenerateBHNoteResponse {
  template_type: string;
  sections: ScribeNoteSections;
  source: "azure_openai" | "rule_based";
  uncertainties?: string[];
}

export interface CDSAlert {
  id: string;
  severity: "high" | "moderate" | "info";
  category: string;
  title: string;
  description: string;
  action_label?: string;
}

export interface ScribeWSEvent {
  event: string;
  timestamp: number;
  data?: Record<string, unknown>;
}

export async function generateBHNote(
  req: GenerateBHNoteRequest,
): Promise<GenerateBHNoteResponse> {
  // Long transcripts take a while to draft, and the server retries Azure on blips.
  const res = await apiClient.post("/ai/generate-bh-note", req, { timeout: 180_000 });
  const payload = res?.data?.data ?? res?.data;
  return payload as GenerateBHNoteResponse;
}

/** Short-lived Azure Speech token for the in-browser conversation transcriber. */
export interface SpeechToken {
  token: string;
  region: string;
  locale: string;
  expires_in_seconds: number;
}

export async function getSpeechToken(): Promise<SpeechToken> {
  const res = await apiClient.get("/ai/scribe/speech-token");
  return (res?.data?.data ?? res?.data) as SpeechToken;
}

/**
 * Checks the same gates as the scribe WebSocket. Returns null when allowed,
 * otherwise a plain-language reason for the clinician. (A refused WebSocket
 * handshake gives the browser no reason at all, hence this preflight.)
 */
export async function checkScribeAccess(): Promise<string | null> {
  // Read the raw denial ourselves: the shared error interceptor rewrites error
  // bodies, dropping the codes below. 401 still throws so session refresh runs.
  let res: { status: number; data?: Record<string, unknown> };
  try {
    res = await apiClient.get("/ai/scribe/access", {
      validateStatus: (s) => s !== 401 && s < 500,
    });
  } catch (err) {
    const status = (err as { status?: number })?.status;
    return status === 401 ? "Your session has expired. Please sign in again." : null;
  }
  if (res.status >= 200 && res.status < 300) return null;
  const data = res.data ?? {};
  const code = typeof data.error === "string" ? data.error : "";
  const message = typeof data.message === "string" ? data.message : "";

  if (code === "provider activity denied") {
    return "AI Scribe is only available to licensed providers. Your account isn't set up as a provider - ask your organization administrator to add you as a provider (with NPI) to use it.";
  }
  if (code === "service_not_enabled") {
    return "AI Scribe isn't turned on for your account. Ask your organization administrator to enable AI features for you.";
  }
  if (res.status === 402) {
    return message || "AI Scribe isn't included in your organization's plan. An administrator can upgrade under Billing.";
  }
  if (res.status === 403) {
    return "Your role doesn't have permission to write clinical notes, so AI Scribe is unavailable. Ask your organization administrator for access.";
  }
  return null;
}

/** Record what happened to an AI draft (audit trail of AI content entering the chart). */
export async function updateScribeSessionStatus(sessionId: string, status: "applied" | "discarded"): Promise<void> {
  await apiClient.patch(`/ai/scribe/sessions/${sessionId}/status`, { status });
}

/** Checkpoint the live transcript so a crash or closed tab can be recovered. */
export async function saveScribeTranscript(sessionId: string, transcript: string): Promise<void> {
  await apiClient.put(`/ai/scribe/sessions/${sessionId}/transcript`, { transcript });
}

/** The server's plain-language reason, e.g. "The AI couldn't draft this note…". */
export function aiErrorMessage(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: { error?: unknown } } })?.response?.data;
  return typeof data?.error === "string" && data.error.trim() ? data.error : fallback;
}

export async function evaluateCDS(input: {
  encounter_id: string;
  template_type: string;
  phq9_score?: number;
  si_spotted?: boolean;
  has_vitals?: boolean;
  sections?: ScribeNoteSections;
}): Promise<CDSAlert[]> {
  const res = await apiClient.post("/ai/cds/evaluate", input);
  const payload = res?.data?.data ?? res?.data;
  return (payload?.alerts ?? []) as CDSAlert[];
}

// ── Post-visit AI insights ──────────────────────────────────────────────────

export interface NoteInsightsRequest {
  encounter_id?: string;
  template_type: string;
  sections: ScribeNoteSections;
  transcript?: string;
  patient_context?: AIChartContext;
}

export interface BillingCode {
  code: string;
  display: string;
  rationale?: string;
}

export interface SuggestBillingResponse {
  diagnoses: BillingCode[];
  em_code: string;
  em_rationale?: string;
  cpt_suggestions?: BillingCode[];
  /** Codes removed by validation, or a note that codes couldn't be verified. */
  warnings?: string[];
  source: "azure_openai" | "unavailable";
}

export interface SuggestedMedication {
  name: string;
  dose?: string;
  frequency?: string;
  sig?: string;
  warnings?: string[];
}

export interface ExtractOrdersResponse {
  labs?: string[];
  imaging?: string[];
  referrals?: string[];
  follow_up?: string;
  medications?: SuggestedMedication[];
  source: "azure_openai" | "rule_based";
}

export interface SafetyHit {
  category: "self_harm" | "harm_others" | "overdose";
  severity: string;
  phrase: string;
  message: string;
}

export interface ScribeSessionRecord {
  id: string;
  encounter_id: string;
  provider_id?: string;
  status: string;
  template_type: string;
  transcript?: string;
  sections_json?: string;
  source?: string;
  created_at: string;
  updated_at: string;
}

export async function suggestBilling(req: NoteInsightsRequest): Promise<SuggestBillingResponse> {
  const res = await apiClient.post("/ai/suggest-billing", req);
  return (res?.data?.data ?? res?.data) as SuggestBillingResponse;
}

export async function extractOrders(req: NoteInsightsRequest): Promise<ExtractOrdersResponse> {
  const res = await apiClient.post("/ai/extract-orders", req);
  return (res?.data?.data ?? res?.data) as ExtractOrdersResponse;
}

export async function labelTranscript(transcript: string): Promise<{ labeled_transcript: string; source: string }> {
  const res = await apiClient.post("/ai/label-transcript", { transcript });
  return (res?.data?.data ?? res?.data) as { labeled_transcript: string; source: string };
}

export interface GenerateAVSResult {
  summary: string;
  source: string;
  /** "Written with help from AI" line; appended unless the clinic turned it off. */
  disclosure?: string;
}

export async function generateAVS(req: NoteInsightsRequest): Promise<GenerateAVSResult> {
  const res = await apiClient.post("/ai/generate-avs", req);
  return (res?.data?.data ?? res?.data) as GenerateAVSResult;
}

export async function listScribeSessions(encounterId: string): Promise<ScribeSessionRecord[]> {
  const res = await apiClient.get("/ai/scribe/sessions", { params: { encounter_id: encounterId } });
  const payload = res?.data?.data ?? res?.data;
  return (payload?.sessions ?? []) as ScribeSessionRecord[];
}

export async function refineSection(input: {
  section_label: string;
  current_text: string;
  instruction: string;
  template_type?: string;
}): Promise<{ refined_text: string; source: string }> {
  const res = await apiClient.post("/ai/refine-section", input);
  return (res?.data?.data ?? res?.data) as { refined_text: string; source: string };
}

export interface DraftPatientEmailRequest {
  patient_id?: string;
  patient_first_name?: string;
  organization_name?: string;
  staff_prompt: string;
  current_subject?: string;
  current_body?: string;
}

export interface DraftPatientEmailResponse {
  subject: string;
  body: string;
  source: "azure_openai" | "rule_based";
  notice: string;
  follow_up_questions?: string[];
}

export async function draftPatientEmail(
  req: DraftPatientEmailRequest,
): Promise<DraftPatientEmailResponse> {
  const res = await apiClient.post("/ai/draft-patient-email", req);
  return (res?.data?.data ?? res?.data) as DraftPatientEmailResponse;
}

/** Transcribe audio buffered during a scribe speech outage (up to 5 minutes). */
export async function transcribeScribeBackfill(wav: Blob, sessionId?: string | null): Promise<{ text: string }> {
  const res = await apiClient.post("/ai/scribe/backfill", wav, {
    headers: { "Content-Type": "audio/wav" },
    // Ties the recovered audio to the visit's patient in the audit log.
    params: sessionId ? { session_id: sessionId } : undefined,
    timeout: 120_000,
  });
  return (res?.data?.data ?? res?.data) as { text: string };
}

export async function transcribeAudio(wav: Blob): Promise<{ text: string; source: string }> {
  const res = await apiClient.post("/ai/transcribe", wav, {
    headers: { "Content-Type": "audio/wav" },
  });
  return (res?.data?.data ?? res?.data) as { text: string; source: string };
}

export function scribeWebSocketUrl(): string {
  return buildWebSocketUrl("/ai/scribe/stream");
}

export function createScribeWebSocket(): WebSocket {
  const token = getAccessToken();
  if (!token) {
    throw new Error("Not authenticated");
  }
  return new WebSocket(scribeWebSocketUrl(), ["ehr-scribe", `jwt.${token}`]);
}
